<template>
  <div class="flex flex-wrap">
    <div class="w-full px-4">
      <div class="rounded-2xl shadow-xl bg-white border border-gray-100 overflow-hidden mb-8">
        <div class="bg-respina-primary px-6 py-6 text-white">
          <h1 class="text-2xl md:text-3xl font-bold mb-1">Data Privacy Policy</h1>
          <p class="text-respina-light text-sm opacity-90">
            How Respina Board collects, uses, and protects your information.
          </p>
        </div>
        <div class="p-6 md:p-8 space-y-6 text-sm leading-relaxed text-gray-700">
          <p>
            This Data Privacy Policy explains how <strong>Respina Board</strong> (the airport and flight
            board application) collects, uses, and protects your personal data across our web
            application and related services. We review and update this policy from time to time
            to reflect changes in our product and applicable privacy laws.
          </p>

          <p>
            In this policy, <strong>“personal data”</strong> means any information that relates to you as an
            identifiable individual — for example your name, email address, IP address, or device
            identifiers. Some information we process is directly tied to you (such as your account
            details), and some is more general (such as analytics on how people use our flight
            board and favorites features).
          </p>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">1. What information we collect</h2>

          <ul class="list-disc pl-5 space-y-1">
            <li>
              <strong>Account &amp; contact information</strong>: when you create an account or sign in,
              we may store your name, email address, hashed password, and basic profile
              information.
            </li>
            <li>
              <strong>Usage data</strong>: searches you perform (airports, flights, airlines), favorites you
              add, and how you interact with different parts of the board UI. We generally store
              this in a way that is linked to your account so we can show and sync your favorites.
            </li>
            <li>
              <strong>Favorites data</strong>: airports and flights that you choose to mark as favorites,
              so we can display them in the sidebar and keep them in sync across devices when
              you are signed in.
            </li>
            <li>
              <strong>Device and network data</strong>: IP address, approximate country, and basic
              device/browser information. We use this to detect whether our service is available
              in your region, to optimise performance, and for security and abuse prevention.
            </li>
            <li>
              <strong>Support and feedback</strong>: if you contact us (for example by email), we keep
              a record of your message and our replies so that we can help you with your request.
            </li>
          </ul>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">2. How we use your information</h2>

          <p>We use your personal data for the following purposes:</p>

          <ul class="list-disc pl-5 space-y-1">
            <li>
              <strong>Service delivery</strong>: to provide the Respina Board experience, including
              searching airports and flights, showing live boards, and managing favorites.
            </li>
            <li>
              <strong>Account and authentication</strong>: to create and maintain your user account,
              keep you signed in securely, and determine which features (such as syncing
              favorites) are available to you.
            </li>
            <li>
              <strong>Regional access and configuration</strong>: to determine whether our service is
              available in your country and to apply regional configuration (for example, using
              geo-information to decide if access should be blocked based on our internal
              policies and legal obligations).
            </li>
            <li>
              <strong>Security and abuse prevention</strong>: to protect our infrastructure, detect and
              prevent misuse or automated abuse, and to keep your account safe.
            </li>
            <li>
              <strong>Analytics and product improvement</strong>: to understand how people use the
              app (for example which searches are common, or how often favorites are used) so
              that we can improve performance, usability, and reliability.
            </li>
            <li>
              <strong>Legal and compliance</strong>: to comply with applicable laws, respond to lawful
              requests, and enforce our terms of service where necessary.
            </li>
          </ul>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">3. Legal bases for processing</h2>

          <p>
            Depending on your location, we rely on one or more of the following legal bases to
            process your personal data:
          </p>

          <ul class="list-disc pl-5 space-y-1">
            <li>
              <strong>Contract</strong>: where processing is necessary to provide the service you have
              requested (for example, creating an account or saving your favorites).
            </li>
            <li>
              <strong>Legitimate interests</strong>: for improving and protecting our service (for
              example, security monitoring and basic analytics), where these interests are not
              overridden by your rights.
            </li>
            <li>
              <strong>Consent</strong>: for optional features such as certain cookies or marketing
              communications. When we rely on consent, you can withdraw it at any time.
            </li>
          </ul>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">4. Cookies and similar technologies</h2>

          <p>
            Respina Board may use cookies and similar technologies to remember your preferences
            (such as API base URL), keep you signed in, and collect anonymised usage statistics.
            You can control cookies through your browser settings. Disabling certain cookies may
            affect how well some parts of the app work.
          </p>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">5. How we share your information</h2>

          <p>We do not sell your personal data. We may share limited information in these cases:</p>

          <ul class="list-disc pl-5 space-y-1">
            <li>
              <strong>Service providers</strong>: trusted third parties that help us run Respina Board
              (for example, hosting providers and infrastructure services). They process data only
              on our instructions and under appropriate data protection safeguards.
            </li>
            <li>
              <strong>Analytics and monitoring</strong>: tools we use to understand performance,
              detect errors, and improve reliability. Where possible, this data is aggregated or
              pseudonymised.
            </li>
            <li>
              <strong>Legal reasons</strong>: where we are required to disclose information in response
              to valid legal process, enforce our terms, or protect our users or the service from
              fraud or security threats.
            </li>
          </ul>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">6. Data retention</h2>

          <p>
            We keep personal data only for as long as needed to provide the service or to meet
            legal, accounting, or security requirements. For example, your account details and
            favorites are kept while your account is active. We may keep anonymised or aggregated
            data (which no longer identifies you) for longer periods to help us understand usage
            trends and improve the product.
          </p>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">7. Your rights and choices</h2>

          <p>
            Depending on your location, you may have the right to access, correct, or delete your
            personal data; to object to certain processing; or to request that we provide your data
            in a portable format. You can exercise many of these rights by:
          </p>

          <ul class="list-disc pl-5 space-y-1">
            <li>Signing out or deleting your account data from within the application (where available).</li>
            <li>Clearing your browser storage or resetting the app on your device.</li>
            <li>Contacting us using the details below.</li>
          </ul>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">8. Security</h2>

          <p>
            We use technical and organisational measures to protect your data, including encrypted
            connections (HTTPS) where appropriate, restricted access to production systems, and
            least-privilege principles for our internal tooling. No system can be perfectly secure,
            but we work to keep Respina Board reasonably protected against unauthorised access,
            disclosure, alteration, or destruction of personal data.
          </p>

          <h2 class="text-base md:text-lg font-bold text-gray-900 mt-4">9. Contact</h2>

          <p>
            If you have questions about this Data Privacy Policy or how we handle your personal
            data in Respina Board, please contact our team via your usual support channel or the
            contact information provided in the application.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
};
</script>

